/**
 * Small formatting helpers the converted screens share, beside lib/money.ts:
 * they read the interface's locale at call time, as lib/money.ts does, so a
 * call site passes nothing.
 */
import { getFormatSettings } from "./core";

// U+2066 LEFT-TO-RIGHT ISOLATE … U+2069 POP DIRECTIONAL ISOLATE.
const LRI = "\u2066";
const PDI = "\u2069";

/**
 * Text that always reads left to right — an IANA zone (`Africa/Cairo`), an
 * identifier someone types in Latin letters (`VehicleInspection`), a
 * version (`v3`) — isolated so a right-to-left line keeps it whole and does
 * not reorder the words around it (docs/help/voice.md, "Mixed-direction
 * text").
 */
export function ltr(text: string): string {
  return `${LRI}${text}${PDI}`;
}

/**
 * A calendar month ("2026-07") as the locale writes it: "July 2026",
 * «يوليو 2026». Gregorian, with Latin digits for Arabic unless the person
 * chose otherwise (lib/i18n/locale.ts). Anything else is returned as given.
 */
export function formatMonth(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat(getFormatSettings().formatLocale, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(date);
}

/**
 * A change on the 0–100 scale with its sign, as the locale writes it:
 * "+4.5%", "-2%", "+0%" (a stat card's movement).
 */
export function formatSignedPercent(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(getFormatSettings().formatLocale, {
    style: "percent",
    signDisplay: "always",
    maximumFractionDigits: 2,
  }).format(value / 100);
}

/**
 * A list's card title, "Customers (12)": the label as the screen translated
 * it and the count in the locale's digits. Composed here rather than through
 * a `{{label}}` placeholder, which would wrap the label in direction
 * isolates: the heading's accessible name stays exactly "Customers (12)".
 * Parentheses mirror on their own in a right-to-left line.
 */
export function listTitle(label: string, count: number): string {
  const digits = new Intl.NumberFormat(getFormatSettings().formatLocale, {
    maximumFractionDigits: 0,
  }).format(count);
  return `${label} (${digits})`;
}
